import { useEffect, useState, type FormEvent } from 'react';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { LoadingState } from '@/components/EmptyState';
import { ErrorBanner } from '@/components/ErrorBanner';
import { RequirePermission } from '@/components/RequirePermission';
import { useAuth } from '@/auth/AuthContext';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatDate } from '@/i18n/format';
import type { ContractView, ContractFormOptions, SaveContractInput } from '@/api/types';
import { contractStatusKey, contractStatusTone, describeContractError } from './contractLabels';

const MAX_PDF_BYTES = 5 * 1024 * 1024;
const STATE_CHOICES: readonly ContractView['state'][] = ['DRAFT', 'ACTIVE', 'SUSPENDED', 'TERMINATED'];

function toBase64(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(bin);
}

/**
 * The contract block of the client card (no e-signature, no EDO): number, date, period, status, tariff
 * and an optional scanned PDF. Creating a contract with a warehouse's legal entity is also how a warehouse
 * is assigned to the client. "In force" is decided by the server (standing); this only shows it.
 */
export function ContractPanel({ ownerId, openRequest, onChanged }: { ownerId: string; openRequest: { n: number; contractId: string | null }; onChanged: () => void }): JSX.Element {
  const { t, locale } = useLanguage();
  const { hasPermission } = useAuth();
  const list = useApi(() => endpoints.ownerContracts(ownerId), [ownerId]);
  const [mode, setMode] = useState<'new' | string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    if (openRequest.n > 0) setMode(openRequest.contractId ?? 'new');
  }, [openRequest]);

  if (list.status === 'loading') return <LoadingState />;
  if (list.status === 'error') return <ErrorBanner error={list.error} onRetry={list.reload} />;
  const contracts: readonly ContractView[] = list.data.contracts ?? [];
  const canWrite = hasPermission('BILLING.WRITE');

  async function after(message: string): Promise<void> {
    setNote(message);
    setError(null);
    setMode(null);
    list.reload();
    onChanged();
  }

  async function upload(c: ContractView, file: File | undefined): Promise<void> {
    if (!file) return;
    setNote(null);
    setError(null);
    if (!(file.type === 'application/pdf' || /\.pdf$/i.test(file.name)) || file.size === 0 || file.size > MAX_PDF_BYTES) {
      setError(t('contract.err.document'));
      return;
    }
    setBusy(c.id);
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      await endpoints.uploadContractDocument(c.id, { filename: file.name, bytesBase64: toBase64(bytes) });
      await after(t('contract.pdf.uploaded'));
    } catch (err) {
      setError(describeContractError(t, err));
    } finally {
      setBusy(null);
    }
  }

  async function download(c: ContractView): Promise<void> {
    setNote(null);
    setError(null);
    setBusy(c.id);
    try {
      const doc = await endpoints.contractDocument(c.id);
      const raw = atob(doc.bytesBase64);
      const bytes = new Uint8Array(raw.length);
      for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(describeContractError(t, err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="card contract-panel" data-testid="contract-panel">
      <div className="contract-head">
        <h2 className="card-title">{t('contract.title')}</h2>
        <RequirePermission permission="BILLING.WRITE">
          {mode === null && (
            <button type="button" className="primary" onClick={() => { setNote(null); setMode('new'); }}>{t('contract.new')}</button>
          )}
        </RequirePermission>
      </div>
      {note && <p className="ok-note" role="status">{note}</p>}
      {error && <div className="error-banner" role="alert">{error}</div>}

      {mode !== null && (
        <ContractForm
          key={mode}
          ownerId={ownerId}
          existing={mode === 'new' ? null : contracts.find((c) => c.id === mode) ?? null}
          onCancel={() => setMode(null)}
          onSaved={() => after(t('contract.saved'))}
        />
      )}

      {contracts.length === 0 && mode === null && <p>{t('contract.empty')}</p>}
      <ul className="contract-list">
        {contracts.map((c) => (
          <li key={c.id} className="contract-item" data-contract={c.id}>
            <div className="contract-item-head">
              <strong>{c.contractNumber ? `${t('onboard.detail.contractNumberOnly', { number: c.contractNumber })}${c.contractDate ? ` · ${formatDate(c.contractDate, locale)}` : ''}` : t('contract.noNumber')}</strong>
              <span className={`badge ${contractStatusTone(c)}`} data-testid="contract-status">{t(contractStatusKey(c) as 'contract.state.ACTIVE')}</span>
            </div>
            <dl className="kv">
              <dt>{t('contract.field.legalEntity')}</dt><dd>{c.legalEntityName}</dd>
              <dt>{t('contract.field.warehouses')}</dt><dd>{c.warehouses.length > 0 ? c.warehouses.map((w) => w.name).join(', ') : t('onboard.detail.warehouseNone')}</dd>
              <dt>{t('contract.field.period')}</dt>
              <dd>
                {c.validTo
                  ? t('contract.periodRange', { from: formatDate(c.validFrom, locale), to: formatDate(c.validTo, locale) })
                  : t('contract.periodOpen', { from: formatDate(c.validFrom, locale) })}
              </dd>
              <dt>{t('contract.field.tariff')}</dt><dd>{c.tariffName ?? t('contract.noTariff')}</dd>
              <dt>{t('contract.field.file')}</dt>
              <dd>{c.hasDocument ? `${c.documentFilename ?? ''}` : t('contract.pdf.none')}</dd>
            </dl>
            <div className="action-row">
              {c.hasDocument && (
                <button type="button" disabled={busy === c.id} onClick={() => void download(c)}>{t('contract.pdf.download')}</button>
              )}
              {canWrite && c.state !== 'TERMINATED' && (
                <>
                  <label className="button-link file-pick">
                    {c.hasDocument ? t('contract.pdf.replace') : t('contract.pdf.upload')}
                    <input type="file" accept="application/pdf,.pdf" disabled={busy === c.id} onChange={(e) => { void upload(c, e.target.files?.[0]); e.target.value = ''; }} />
                  </label>
                  <button type="button" onClick={() => { setNote(null); setMode(c.id); }}>{t('common.edit')}</button>
                </>
              )}
            </div>
            {c.state === 'TERMINATED' && <p className="field-hint">{t('contract.terminatedNote')}</p>}
            {canWrite && c.state !== 'TERMINATED' && <p className="field-hint">{t('contract.pdf.hint')}</p>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ContractForm({ ownerId, existing, onCancel, onSaved }: { ownerId: string; existing: ContractView | null; onCancel: () => void; onSaved: () => Promise<void> }): JSX.Element {
  const { t } = useLanguage();
  const options = useApi<ContractFormOptions>(() => endpoints.contractFormOptions(), []);
  const [warehouseId, setWarehouseId] = useState('');
  const [number, setNumber] = useState(existing?.contractNumber ?? '');
  const [date, setDate] = useState(existing?.contractDate ?? '');
  const [validFrom, setValidFrom] = useState(existing ? existing.validFrom.slice(0, 10) : '');
  const [validTo, setValidTo] = useState(existing?.validTo ? existing.validTo.slice(0, 10) : '');
  const [state, setState] = useState<ContractView['state']>(existing?.state === 'ON_HOLD' ? 'SUSPENDED' : existing?.state ?? 'DRAFT');
  const [tariffId, setTariffId] = useState(existing?.tariffId ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const whs: ContractFormOptions['warehouses'] = options.status === 'success' ? options.data.warehouses ?? [] : [];
  useEffect(() => {
    if (!existing && whs.length === 1 && warehouseId === '') setWarehouseId(whs[0]!.id);
  }, [existing, whs, warehouseId]);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    if (!existing && !warehouseId) { setError(t('contract.err.generic')); return; }
    const input: SaveContractInput = {
      contractNumber: number.trim() === '' ? null : number.trim(),
      contractDate: date === '' ? null : date,
      ...(validFrom ? { validFrom } : {}),
      validTo: validTo === '' ? null : validTo,
      state,
      tariffId: tariffId === '' ? null : tariffId,
    };
    setSaving(true);
    try {
      if (existing) await endpoints.updateClientContract(existing.id, input);
      else await endpoints.createClientContract({ ...input, ownerId, warehouseId });
      await onSaved();
    } catch (err) {
      setError(describeContractError(t, err));
    } finally {
      setSaving(false);
    }
  }

  if (options.status === 'loading') return <LoadingState />;
  if (options.status === 'error') return <ErrorBanner error={options.error} onRetry={options.reload} />;
  const tariffList: ContractFormOptions['tariffs'] = options.data.tariffs ?? [];

  return (
    <form className="sub-form contract-form" onSubmit={(e) => void submit(e)} aria-label={existing ? t('contract.formEdit') : t('contract.formNew')}>
      <h3>{existing ? t('contract.formEdit') : t('contract.formNew')}</h3>
      {error && <div className="error-banner" role="alert">{error}</div>}
      {!existing && (
        <div className="field-row">
          <label htmlFor="contract-warehouse">{t('contract.field.warehouse')}</label>
          <select id="contract-warehouse" value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} required>
            <option value="" disabled>—</option>
            {whs.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        </div>
      )}
      <div className="field-row">
        <label htmlFor="contract-number">{t('contract.field.number')}</label>
        <input id="contract-number" value={number} maxLength={80} onChange={(e) => setNumber(e.target.value)} autoComplete="off" />
      </div>
      <div className="field-row">
        <label htmlFor="contract-date">{t('contract.field.date')}</label>
        <input id="contract-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </div>
      <div className="field-row">
        <label htmlFor="contract-from">{t('contract.field.validFrom')}</label>
        <input id="contract-from" type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
      </div>
      <div className="field-row">
        <label htmlFor="contract-to">{t('contract.field.validTo')}</label>
        <input id="contract-to" type="date" value={validTo} onChange={(e) => setValidTo(e.target.value)} />
      </div>
      <div className="field-row">
        <label htmlFor="contract-state">{t('contract.field.state')}</label>
        <select id="contract-state" value={state} onChange={(e) => setState(e.target.value as ContractView['state'])}>
          {STATE_CHOICES.map((s) => <option key={s} value={s}>{t(`contract.state.${s}` as const)}</option>)}
        </select>
        <p className="field-hint">{t('contract.activationHint')}</p>
      </div>
      <div className="field-row">
        <label htmlFor="contract-tariff">{t('contract.field.tariff')}</label>
        <select id="contract-tariff" value={tariffId} onChange={(e) => setTariffId(e.target.value)}>
          <option value="">{t('contract.noTariff')}</option>
          {tariffList.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
        </select>
      </div>
      <div className="form-actions">
        <button type="submit" className="primary" disabled={saving}>{saving ? t('common.saving') : t('contract.save')}</button>
        <button type="button" onClick={onCancel} disabled={saving}>{t('common.cancel')}</button>
      </div>
    </form>
  );
}
